import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { getUserAdoptionDetail } from '../../lib/api';
import { Card, CardContent } from '../ui/card';

export default function UserDetailDialog({ user, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user || !open) return;
    setLoading(true);
    getUserAdoptionDetail(user.id).then(setData).finally(() => setLoading(false));
  }, [user, open]);

  if (!user) return null;

  // Build a simple 30-day sparkline data
  const days = data?.daily_counts || {};
  const sparkPoints = [];
  const today = new Date();
  let max = 1;
  for (let i = 29; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const v = days[key] || 0;
    sparkPoints.push(v);
    if (v > max) max = v;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="user-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-lg bg-[#E63935]/20 text-[#E63935] flex items-center justify-center text-sm font-bold">{user.initials}</span>
            <div>
              <p className="text-lg font-bold text-foreground">{user.name}</p>
              <p className="text-xs text-muted-foreground font-normal">{user.role} · {user.status}</p>
            </div>
          </DialogTitle>
        </DialogHeader>
        {loading || !data ? (
          <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" /></div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Card className="bg-background border-border"><CardContent className="p-3">
                <p className="text-[10px] text-muted-foreground">Adoption Score</p><p className="text-xl font-bold text-foreground mt-0.5">{user.adoption_score}</p>
              </CardContent></Card>
              <Card className="bg-background border-border"><CardContent className="p-3">
                <p className="text-[10px] text-muted-foreground">This Week</p><p className="text-xl font-bold text-foreground mt-0.5">{data.sessions_this_week}</p>
              </CardContent></Card>
              <Card className="bg-background border-border"><CardContent className="p-3">
                <p className="text-[10px] text-muted-foreground">Last 30 Days</p><p className="text-xl font-bold text-foreground mt-0.5">{data.sessions_last_30_days}</p>
              </CardContent></Card>
              <Card className="bg-background border-border"><CardContent className="p-3">
                <p className="text-[10px] text-muted-foreground">Total Sessions</p><p className="text-xl font-bold text-foreground mt-0.5">{data.total_sessions}</p>
              </CardContent></Card>
            </div>

            <Card className="bg-background border-border"><CardContent className="p-3">
              <p className="text-xs font-bold text-foreground mb-2">Daily activity (last 30 days)</p>
              <svg viewBox="0 0 300 40" className="w-full h-16" preserveAspectRatio="none">
                {sparkPoints.map((v, i) => (
                  <rect key={i} x={i * 10} y={40 - (v / max) * 38} width="8" height={(v / max) * 38 || 1} fill="#E63935" opacity={v ? 0.85 : 0.15} />
                ))}
              </svg>
            </CardContent></Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Card className="bg-background border-border"><CardContent className="p-3">
                <p className="text-xs font-bold text-foreground mb-2">Features used</p>
                {data.features.length === 0 ? <p className="text-xs text-muted-foreground">None yet</p> : (
                  <div className="space-y-1">{data.features.map(f => (
                    <div key={f.key} className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{f.label}</span><span className="font-bold text-foreground">{f.count}</span>
                    </div>
                  ))}</div>
                )}
              </CardContent></Card>
              <Card className="bg-background border-border"><CardContent className="p-3">
                <p className="text-xs font-bold text-foreground mb-2">Recent activity</p>
                {data.recent_activity.length === 0 ? <p className="text-xs text-muted-foreground">None yet</p> : (
                  <div className="space-y-1 max-h-40 overflow-y-auto">{data.recent_activity.map((a, i) => (
                    <div key={i} className="text-[11px] flex items-center justify-between gap-2">
                      <span className="text-foreground truncate">{a.workflow_label}{a.borrower ? ` · ${a.borrower}` : ''}</span>
                      <span className="text-muted-foreground whitespace-nowrap">{a.created_at ? new Date(a.created_at).toLocaleDateString([], { month: 'short', day: 'numeric' }) : ''}</span>
                    </div>
                  ))}</div>
                )}
              </CardContent></Card>
            </div>

            {(user.status === 'At Risk' || user.status === 'Inactive' || user.status === 'Not Activated') && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
                <p className="text-xs font-bold text-amber-500 mb-1">Suggested coaching</p>
                <p className="text-[11px] text-muted-foreground">
                  {user.status === 'Not Activated'
                    ? `${user.name} has not used Clara yet. Send a quick start guide and share a live example from a Power User.`
                    : user.last_used_days_ago > 7
                      ? `${user.name} was previously active but hasn't used Clara in ${user.last_used_days_ago} days. Check in and pair on one real workflow.`
                      : `${user.name}'s activity is light. Show them how ${user.primary_use || 'Client Analysis'} saves time and drives referrals.`}
                </p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
